import { lazy, ComponentType } from "react";

/**
 * React.lazy wrapper that recovers from stale chunk hashes after a redeploy.
 *
 * Symptom this fixes: user leaves the tab open, we ship a new build, the old
 * chunk URLs vanish, dynamic import() rejects with ChunkLoadError /
 * "Failed to fetch dynamically imported module" — Suspense renders nothing
 * and the user sees a white page.
 *
 * Strategy: on the first such failure in this tab, force a one-shot
 * window.location.reload() to fetch the new index.html (and new chunk URLs).
 * Guarded by sessionStorage so we never loop.
 */
const RELOAD_FLAG = "lazy-chunk-reloaded";

function isChunkLoadError(err: unknown): boolean {
  const msg =
    err instanceof Error ? `${err.name} ${err.message}` : String(err ?? "");
  return (
    /ChunkLoadError/i.test(msg) ||
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /dynamically imported module/i.test(msg)
  );
}

export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (isChunkLoadError(err)) {
        try {
          if (!sessionStorage.getItem(RELOAD_FLAG)) {
            sessionStorage.setItem(RELOAD_FLAG, "1");
            window.location.reload();
            // Return a never-resolving promise so Suspense keeps the
            // fallback while the reload happens.
            return await new Promise<{ default: T }>(() => {});
          }
        } catch {
          /* sessionStorage may be unavailable — fall through */
        }
      }
      throw err;
    }
  });
}

/** Clear the reload guard once the app successfully boots. */
export function clearLazyReloadGuard() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    /* ignore */
  }
}
